import {
  WixAtlasServiceWeb,
  Proximity,
} from '@wix/ambassador-wix-atlas-service-web/http';
import type {
  CommonAddress,
  Prediction,
  SearchResult,
} from '@wix/ambassador-wix-atlas-service-web/types';

export { Prediction, CommonAddress };

export type GetPlaceParams = {
  searchId: string;
};
export type GetPlace = (
  params: GetPlaceParams,
) => Promise<CommonAddress | undefined>;

export type PredictParams = {
  input: string;
  countryCodes?: string[];
};
export type Predict = (
  params: PredictParams,
) => Promise<Prediction[] | undefined>;

export type SearchByPostalCodeParams = {
  postalCode: string;
  countryCode?: string;
};
export type SearchByPostalCode = (
  params: SearchByPostalCodeParams,
) => Promise<CommonAddress | undefined>;

export type AtlasService = {
  predict: Predict;
  getPlace: GetPlace;
  searchByPostalCode: SearchByPostalCode;
};

export type CreateAtlasServiceParams = {
  getInstance: () => string;
  sessionToken: string;
  refreshSessionToken: () => void;
  language?: string;
  locale?: string;
  atlasBaseUrl?: string;
};

export const ATLAS_BASE_URL = '/_api/wix-atlas-service-web';

export function createAtlasService({
  getInstance,
  sessionToken,
  refreshSessionToken,
  language,
  locale,
  atlasBaseUrl = ATLAS_BASE_URL,
}: CreateAtlasServiceParams): AtlasService {
  const atlas = WixAtlasServiceWeb(atlasBaseUrl);

  const predict: Predict = async ({ input, countryCodes }) => {
    const headers = getHeaders({ getInstance, language, locale });
    const autocompleteService = atlas.AutocompleteServiceV2()(headers);
    const { predictions } = await autocompleteService.predict({
      sessionToken,
      input,
      countryCodes,
    });

    return predictions ?? [];
  };

  const getPlace: GetPlace = async ({ searchId }) => {
    const headers = getHeaders({ getInstance, language, locale });
    const placesService = atlas.PlacesServiceV2()(headers);
    const { place } = await placesService.getPlace({
      searchId,
      sessionToken,
    });
    refreshSessionToken();
    return place?.address;
  };

  const searchByPostalCode: SearchByPostalCode = async ({
    postalCode,
    countryCode,
  }) => {
    const headers = getHeaders({ getInstance, language, locale });
    const locationService = atlas.LocationServiceV2()(headers);
    const { searchResults } = await locationService.search({
      zipCode: postalCode,
      ...(countryCode ? { countryCodes: [countryCode] } : {}),
    });
    if (!searchResults?.[0]) {
      return;
    }
    const pinpointAddress = getByProximity(searchResults, Proximity.PINPOINT);
    const approximateAddress = getByProximity(
      searchResults,
      Proximity.APPROXIMATE,
    );
    const generalAreaAddress = getByProximity(
      searchResults,
      Proximity.GENERAL_AREA,
    );
    return (
      pinpointAddress ??
      approximateAddress ??
      generalAreaAddress ??
      searchResults[0].address
    );
  };

  return { predict, getPlace, searchByPostalCode };
}

type GetHeadersParams = {
  getInstance: () => string;
  language?: string;
  locale?: string;
};
type Headers = { [header: string]: string };

function getHeaders({
  getInstance,
  language = 'en',
  locale = 'en-us',
}: GetHeadersParams): Headers {
  const instance = getInstance();
  return {
    Authorization: instance,
    // 'x-wix-linguist': language|locale|isPrimaryLanguage|token
    'x-wix-linguist': `${language}|${locale}|false|${instance}`,
  };
}

function getByProximity(searchResults: SearchResult[], proximity: Proximity) {
  const address = searchResults.find(
    (result) => result.proximity === proximity,
  )?.address;
  return address;
}
